import { getTranslation } from '../../helpers';
import { openModal } from '../../helpers/modals';

const selectors = {
  bisTrigger: '[data-product-bis-trigger]',
  bisEmail: '[data-product-bis-email]',
  bisSubmit: '[data-product-bis-submit]',
  bisErrorMsg: '[data-product-bis-error-msg]',
};

/**
 * Product back-in-stock modal + Klaviyo submission.
 */
export default class ProductBackInStock {
  constructor(formElement) {
    this.formElement = formElement;
    this.bisTrigger = this.formElement?.querySelector(selectors.bisTrigger);

    if (!this.bisTrigger) {
      return;
    }

    this._init();
  }

  _init() {
    this.bisTrigger.addEventListener('click', e => {
      e.preventDefault();
      const opened = openModal('product-bis-modal');
      if (!opened) {
        return;
      }
      this._bindBackInStockSubmission();
    });
  }

  _bindBackInStockSubmission() {
    const modal = document.getElementById('product-bis-modal');
    if (!modal) {
      return;
    }

    const bisEmail = modal.querySelector(selectors.bisEmail);
    const bisSubmit = modal.querySelector(selectors.bisSubmit);
    const bisErrorMsg = modal.querySelector(selectors.bisErrorMsg);
    const variantId = this.bisTrigger?.dataset.productBisVariantId;

    if (!bisEmail || !bisSubmit || !bisErrorMsg || !variantId) {
      return;
    }

    const updateMessage = (message, isError = true) => {
      bisErrorMsg.textContent = message;
      bisErrorMsg.classList.remove('hidden');
      bisErrorMsg.classList.toggle('text-lof-red', isError);
      bisErrorMsg.classList.toggle('text-black', !isError);
    };

    const clearMessage = () => {
      bisErrorMsg.textContent = '';
      bisErrorMsg.classList.add('hidden');
      bisErrorMsg.classList.add('text-lof-red');
      bisErrorMsg.classList.remove('text-black');
    };

    const handleSubmit = async e => {
      e.preventDefault();
      const email = bisEmail.value.trim();

      if (!this._isValidEmail(email)) {
        updateMessage(getTranslation('products.waitlist.email_error'));
        return;
      }

      const companyId = window.klaviyoApiKey || '';
      if (!companyId) {
        updateMessage(getTranslation('products.waitlist.error'));
        return;
      }

      const listId = window.klaviyoListId || '';

      bisSubmit.setAttribute('disabled', 'disabled');
      try {
        const bisResponse = await fetch(
          `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${companyId}`,
          {
            method: 'POST',
            headers: {
              accept: 'application/json',
              'Content-Type': 'application/json',
              revision: '2024-05-15',
            },
            body: JSON.stringify({
              data: {
                type: 'back-in-stock-subscription',
                attributes: {
                  profile: {
                    data: {
                      type: 'profile',
                      attributes: {
                        email,
                      },
                    },
                  },
                  channels: ['EMAIL'],
                },
                relationships: {
                  variant: {
                    data: {
                      type: 'catalog-variant',
                      id: `$shopify:::$default:::${variantId}`,
                    },
                  },
                },
              },
            }),
          },
        );

        if (!bisResponse.ok) {
          updateMessage(getTranslation('products.waitlist.error'));
          return;
        }

        if (listId) {
          await fetch(
            `https://a.klaviyo.com/client/subscriptions/?company_id=${companyId}`,
            {
              method: 'POST',
              headers: {
                revision: '2024-07-15',
                'content-type': 'application/json',
              },
              body: JSON.stringify({
                data: {
                  type: 'subscription',
                  attributes: {
                    profile: {
                      data: {
                        type: 'profile',
                        attributes: {
                          properties: {
                            SubscribeSource: 'Back In Stock Popup',
                          },
                          email,
                        },
                      },
                    },
                  },
                  relationships: {
                    list: { data: { type: 'list', id: listId } },
                  },
                },
              }),
            },
          );
        }

        updateMessage(getTranslation('products.waitlist.success'), false);
      } catch (error) {
        console.error(error);
        updateMessage(getTranslation('products.waitlist.error'));
      } finally {
        bisSubmit.removeAttribute('disabled');
      }
    };

    bisSubmit.onclick = handleSubmit;
    bisEmail.oninput = clearMessage;
  }

  _isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }
}
